import { useQuery } from '@tanstack/react-query';
import { Alert, Button } from 'react-bootstrap';
import { BsPencilSquare } from 'react-icons/bs';
import { LinkContainer } from 'react-router-bootstrap';
import { Outlet } from 'react-router-dom';
import { StatusCodes } from '../../../../v2/js/constants/workflows';
import { MAIN_CONTENT_AREA } from '../../../../v2/js/utils/layoutStyles';
import { ProviderService, SourceTypeEnum } from '../../../api';
import { Card } from '../../../app/components/Card';
import { ProfileProgress } from '../../../app/components/ProfileProgress';
import { ProviderProfileHeader } from '../../../app/components/ProviderProfileHeader';
import { TabsNavbar } from '../../../app/components/TabsNavbar';
import { ProviderLimitContext } from '../../../app/contexts/ProviderLimitContext';
import { useCurrentUser } from '../../../app/hooks/auth';
import { useUserProgress } from '../../../app/hooks/user-progress';

export const CurrentProviderProfile = () => {
  const {
    isLoading: isProviderLoading,
    error: providerFetchError,
    data: provider,
  } = useQuery({
    queryKey: ['provider', 'detail', 'current'],
    queryFn: async () => await ProviderService.providerCurrentRetrieve(),
  });
  const { user } = useCurrentUser();
  const { progress, isProfileComplete } = useUserProgress(
    user?.provider_completed_registration_steps
  );
  const isSourcedProvider = provider?.source_type === SourceTypeEnum.ORGANIZATION;

  const callToActionEdit = (
    <LinkContainer to="/app/provider/edit">
      <Button
        className="d-flex align-items-center justify-content-center px-3 py-2 mb-2 w-100"
        size="lg"
        variant="primary"
      >
        <BsPencilSquare className="me-2" /> Edit Profile
      </Button>
    </LinkContainer>
  );

  const callToActionComplete = (
    <div className="h-100 d-flex align-items-end justify-content-end">
      <div className="d-flex flex-column align-items-end pt-4">
        <ProfileProgress progress={progress} className="mx-auto mb-4" />
      </div>
    </div>
  );

  return (
    <>
      <div className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden">
        {/* Main content area */}
        <div className="tw:flex tw:flex-col tw:overflow-hidden tw:w-full">
          <main className={MAIN_CONTENT_AREA}>
            {!!providerFetchError && (
              <Alert variant="danger" show={!!providerFetchError}>
                <Alert.Heading>Something went wrong</Alert.Heading>
                <p>Please try again later. If the problem persists, please contact us.</p>
              </Alert>
            )}
            <ProviderProfileHeader
              provider={provider}
              isProviderLoading={isProviderLoading}
              isCurrentProvider
              isProfileComplete={isProfileComplete}
              hasAcceptedConnection={
                (provider?.clearance_status &&
                  provider?.clearance_status !== StatusCodes.NOT_CLEARED) ||
                false
              }
              callToAction={
                !isProfileComplete && !isSourcedProvider ? callToActionComplete : callToActionEdit
              }
            />
            <Card className="mb-4">
              <TabsNavbar
                alignment="center"
                tabs={[
                  {
                    label: 'Credentials',
                    to: '/app/provider/profile/credentials',
                  },
                  {
                    label: 'Professional Traits',
                    to: '/app/provider/profile/professional-traits',
                  },
                  {
                    label: 'Education',
                    to: '/app/provider/profile/education',
                  },
                  {
                    label: 'Licenses & Certifications',
                    to: '/app/provider/profile/licenses',
                  },
                  {
                    label: 'Other',
                    to: '/app/provider/profile/other',
                  },
                ]}
              />
              <div className="pt-4 px-4">
                <ProviderLimitContext.Provider value={{ providerLimit: provider ?? null }}>
                  <Outlet />
                </ProviderLimitContext.Provider>
              </div>
            </Card>
          </main>
        </div>
      </div>
      {/* <ProfileCompleteDialog provider={provider} show={true} closeDialog={() => {}}/> */}
    </>
  );
};
